import * as React from 'react';

import { compact, concat, groupBy, isEmpty, size } from 'lodash';
import pluralize from 'pluralize';

import AdminNoticeBanner from 'admin/components/AdminNoticeBanner';
import Copy from 'components/Copy';
import Heading from 'components/Heading';
import Link from 'components/Link';
import Row from 'components/Row';
import Text from 'components/Text';
import LinkConstants from 'constants/LinkConstants';

import { AdminDistrictSisDashboard_organizations_sisSyncOverview_districtStatus_uploadErrors as UploadError } from 'admin/rostering/queries/__generated__/AdminDistrictSisDashboard';

type Props = {
  uploadErrors: UploadError[];
  filesZipUrl?: string;
};

type ErrorComponentElements = 'div' | 'ol' | 'li';

export default function AdminRenderUploadErrorsBanner(props: Props) {
  const { uploadErrors, filesZipUrl } = props;

  function UploadErrorsBannerDetailsList({
    children,
    title,
    element,
  }: {
    children: React.ReactNode;
    title: string;
    element: ErrorComponentElements;
  }) {
    const Component = element;
    return (
      <React.Fragment>
        <Heading level="3" levelStyle="6">
          {title}
        </Heading>
        <Component>{children}</Component>
      </React.Fragment>
    );
  }

  function UploadErrorsBannerErrorListItem({
    copy,
    errors,
    name,
    element,
  }: {
    copy: React.ReactFragment;
    errors: UploadError[];
    name: string;
    element: ErrorComponentElements;
  }) {
    const Component = element;
    return (
      <Component>
        <p>
          <Text weight="semibold">{name}</Text>: {copy}
        </p>
        <UploadErrorsBannerErrorsFileList errors={errors} />
      </Component>
    );
  }

  function UploadErrorsBannerErrorsFileList({ errors }: { errors: UploadError[] }) {
    if (errors.length === 0) {
      return null;
    }
    return (
      <ol>
        {errors.map(item => (
          <React.Fragment>
            <li key={item.fileName}>
              {item.fileName}
              {item.downloadUrl && (
                <p>
                  <Link weight="semibold" href={item.downloadUrl} download>
                    Download CSV of errors
                  </Link>
                </p>
              )}
            </li>
          </React.Fragment>
        ))}
      </ol>
    );
  }

  const filterInvalids = (theList?: UploadError[]): UploadError[] => {
    if (!theList) {
      return [];
    }
    return theList;
  };

  const groupedErrors = groupBy(uploadErrors, 'error');
  const errorsUnknownFormat = filterInvalids(groupedErrors['unknown_format']);
  const errorsDuplicateIds = filterInvalids(groupedErrors['duplicate_ids']);
  const errorsInvalid = filterInvalids(groupedErrors['invalid']);
  const errorsMismatchedIds = filterInvalids(groupedErrors['mismatched_ids']);
  const errorsMissingIds = filterInvalids(groupedErrors['missing_ids']);
  const errorsInvalidColumnsForRow = filterInvalids(groupedErrors['invalid_columns_for_row']);

  // For clarity, we group some types of errors for the user.
  // There is a chance that no errors with the specified key exist,
  // so we need to remove null values
  const errorsEmpty = compact(concat(groupedErrors['empty'], groupedErrors['incorrect_headers']));
  const errorsMissing = compact(concat(groupedErrors['missing'], groupedErrors['empty']));

  let containerElement: ErrorComponentElements = 'div';
  let errorElement: ErrorComponentElements = 'div';

  if (Object.keys(groupedErrors).length > 1) {
    containerElement = 'ol';
    errorElement = 'li';
  }

  return (
    <AdminNoticeBanner
      color="lava"
      icon="explanation-circle"
      actionElement={null}
      details={
        <Copy>
          <Row size="medium">
            <UploadErrorsBannerDetailsList
              title={pluralize('Error', uploadErrors.length)}
              element={containerElement}>
              {!isEmpty(errorsUnknownFormat) && (
                <UploadErrorsBannerErrorListItem
                  name="Unknown Format"
                  element={errorElement}
                  copy={
                    <React.Fragment>
                      We’re unable to determine which CSV format you’re using. Upload a schools.csv
                      if you are following the Remind CSV format, or upload both a manifest.csv and
                      orgs.csv if you are following the OneRoster CSV format. If you sent a zip
                      file, please resend each CSV file separately.
                    </React.Fragment>
                  }
                  errors={[]}
                />
              )}
              {errorsInvalidColumnsForRow.length > 0 && (
                <UploadErrorsBannerErrorListItem
                  name="Formatting Error"
                  element={errorElement}
                  copy={
                    <React.Fragment>
                      1 or more of your rows has an extra value, most likely due to a stray comma.
                      Review and remove any stray commas.
                    </React.Fragment>
                  }
                  errors={errorsInvalidColumnsForRow}
                />
              )}
              {!isEmpty(errorsInvalid) && (
                <UploadErrorsBannerErrorListItem
                  name="Unreadable Encoding"
                  element={errorElement}
                  copy={
                    <React.Fragment>
                      We’re unable to read the {pluralize('file', size(errorsInvalid))} you
                      uploaded.
                    </React.Fragment>
                  }
                  errors={errorsInvalid}
                />
              )}
              {!isEmpty(errorsEmpty) && (
                <UploadErrorsBannerErrorListItem
                  name="Invalid Content"
                  element={errorElement}
                  copy={
                    <React.Fragment>
                      The content in the {pluralize('file', size(errorsEmpty))} isn’t correct.{' '}
                      {errorsEmpty.length > 1
                        ? 'Make sure they contain the appropriate headers.'
                        : 'Make sure it contains the appropriate headers.'}
                    </React.Fragment>
                  }
                  errors={errorsEmpty}
                />
              )}
              {!isEmpty(errorsMissing) && (
                <UploadErrorsBannerErrorListItem
                  name="Missing Files"
                  element={errorElement}
                  copy={
                    <React.Fragment>
                      We were unable to locate the following{' '}
                      {pluralize('file', size(errorsMissing))}.
                    </React.Fragment>
                  }
                  errors={errorsMissing}
                />
              )}
              {!isEmpty(errorsDuplicateIds) && (
                <UploadErrorsBannerErrorListItem
                  name="Duplicate IDs"
                  element={errorElement}
                  copy={
                    <React.Fragment>
                      We’ve detected duplicate reference IDs in the following{' '}
                      {pluralize('file', size(errorsDuplicateIds))}. Each ID must be unique.
                    </React.Fragment>
                  }
                  errors={errorsDuplicateIds}
                />
              )}
              {!isEmpty(errorsMissingIds) && (
                <UploadErrorsBannerErrorListItem
                  name="Missing IDs"
                  element={errorElement}
                  copy={
                    <React.Fragment>
                      We were unable to find expected IDs in the following{' '}
                      {pluralize('file', size(errorsMissingIds))}.
                    </React.Fragment>
                  }
                  errors={errorsMissingIds}
                />
              )}
              {!isEmpty(errorsMismatchedIds) && (
                <UploadErrorsBannerErrorListItem
                  name="Mismatched IDs"
                  element={errorElement}
                  copy="We’ve encountered conflicts in IDs across the following files. Ensure they’re consistent."
                  errors={errorsMismatchedIds}
                />
              )}
            </UploadErrorsBannerDetailsList>
          </Row>
          <UploadErrorsBannerDetailsList title="Next steps" element="ol">
            <li>
              <p>
                <Link weight="semibold" href={filesZipUrl} download>
                  Download the files we received.
                </Link>
              </p>
            </li>
            <li>
              <p>
                Refer to{' '}
                <Link weight="semibold" href={LinkConstants.DIRECT_SYNC}>
                  this support article
                </Link>{' '}
                and make sure your CSVs are configured correctly.
              </p>
            </li>
            <li>
              <p>Upload all the CSVs, with revisions, via SFTP again.</p>
            </li>
          </UploadErrorsBannerDetailsList>
        </Copy>
      }>
      We’ve encountered an error with one or more of your CSVs.
    </AdminNoticeBanner>
  );
}
